import { EQUIPMENT_GRID_SQUARES } from 'data/constants';
import { GridObjectType, LevelType, PositionType } from 'data/types';
import {
  EquipmentAttachmentSideType,
  EquipmentRotationContextType
} from 'utils/equipment-rotation-types';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import { isWallMountedAirHandler } from 'utils/air-handler-mounting';
import {
  buildWallSegments,
  segmentRotationDeg,
  signedDistanceToSegmentLine,
  unclampedOffsetOnSegment
} from 'utils/wall-segment';
import { WallSegmentType } from 'utils/wall-segment-types';
import { exteriorSnapSegments, spaceSnapPolygons } from 'utils/wall-snap';

const ATTACH_TOLERANCE_FT = 0.5;
const SIDE_SLACK_FT = 0.05;

export function equipmentRotationContext(
  level: LevelType | undefined
): EquipmentRotationContextType {
  const spaces = level?.sections.flatMap((section) => section.spaces) ?? [];
  return {
    polygons: spaceSnapPolygons(spaces),
    footprints: getEffectiveFootprintPolygons(level)
  };
}

export function rotationCandidateSegments(
  context: EquipmentRotationContextType,
  side: EquipmentAttachmentSideType
): WallSegmentType[] {
  if (side === 'outside') {
    return exteriorSnapSegments(context.polygons, {
      footprints: context.footprints
    });
  }
  return context.polygons.flatMap((polygon) =>
    buildWallSegments(polygon.vertices, null)
  );
}

function signedDistanceQualifies(
  signed: number,
  halfFt: number,
  side: EquipmentAttachmentSideType
): boolean {
  const reach = halfFt + ATTACH_TOLERANCE_FT;
  if (side === 'inside') {
    return signed >= -reach && signed <= SIDE_SLACK_FT;
  }
  if (side === 'outside') {
    return signed >= -SIDE_SLACK_FT && signed <= reach;
  }
  return Math.abs(signed) <= reach;
}

export function nearestAttachedSegment(
  center: PositionType,
  sizeFt: number,
  segments: WallSegmentType[],
  side: EquipmentAttachmentSideType
): WallSegmentType | null {
  const halfFt = sizeFt / 2;
  let best: WallSegmentType | null = null;
  let bestDistance = Infinity;
  for (const segment of segments) {
    const signed = signedDistanceToSegmentLine(segment, center);
    const offset = unclampedOffsetOnSegment(segment, center);
    const withinSpan = offset >= -halfFt && offset <= segment.lengthFt + halfFt;
    const qualifies =
      withinSpan && signedDistanceQualifies(signed, halfFt, side);
    const distance = Math.abs(signed);
    if (qualifies && distance < bestDistance) {
      best = segment;
      bestDistance = distance;
    }
  }
  return best;
}

export function equipmentRotationDeg(
  center: PositionType,
  sizeFt: number,
  context: EquipmentRotationContextType,
  side: EquipmentAttachmentSideType
): number {
  const segment = nearestAttachedSegment(
    center,
    sizeFt,
    rotationCandidateSegments(context, side),
    side
  );
  return segment ? segmentRotationDeg(segment) : 0;
}

export function attachmentSideForGridObject(
  object: Pick<GridObjectType, 'type' | 'airHandlerTypeCode' | 'placementType'>
): EquipmentAttachmentSideType | null {
  if (object.type === 'air-handler') {
    return object.airHandlerTypeCode &&
      isWallMountedAirHandler(object.airHandlerTypeCode)
      ? 'inside'
      : null;
  }
  if (object.type === 'condenser') {
    return object.placementType === 'roof' ? null : 'outside';
  }
  if (object.type === 'electrical-panel') {
    return 'either';
  }
  return null;
}

export function gridObjectRotationDeg(
  object: GridObjectType,
  context: EquipmentRotationContextType
): number {
  const side = attachmentSideForGridObject(object);
  if (!side) {
    return 0;
  }
  const sizeFt = object.gridSquares ?? EQUIPMENT_GRID_SQUARES;
  const center = {
    x: object.position.x + sizeFt / 2,
    y: object.position.y + sizeFt / 2
  };
  return equipmentRotationDeg(center, sizeFt, context, side);
}

export function withGridObjectRotation(
  objects: GridObjectType[],
  context: EquipmentRotationContextType
): GridObjectType[] {
  return objects.map((object) => ({
    ...object,
    rotation: gridObjectRotationDeg(object, context)
  }));
}

export function squareVisualRotationDeg(rotationDeg: number): number {
  const folded = ((rotationDeg % 90) + 90) % 90;
  return folded > 45 ? folded - 90 : folded;
}
